import { Component, DestroyRef, inject, signal, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { TagModule } from 'primeng/tag';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { CashService } from '../../core/services/cash.service';
import { StoreService } from '../../core/services/store.service';
import { StoreLiteDto } from '../../core/models/store.models';
import { storePicker } from '../../core/utils/store-picker';
import {
  CashBalanceDto,
  CashFlowEntryDto,
  CashTransactionType,
  PaymentMethod,
} from '../../core/models/finance.models';
import { MoneyFixedPipe } from '../../shared/pipes/money.pipe';
import { CurrencyService } from '../../core/services/currency.service';

@Component({
  selector: 'app-cash',
  standalone: true,
  imports: [
    MoneyFixedPipe,
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    SelectModule,
    InputNumberModule,
    InputTextModule,
    DatePickerModule,
    TagModule,
    TranslatePipe,
  ],
  templateUrl: './cash.component.html',
})
export class CashComponent implements OnInit {
  private svc = inject(CashService);
  private storeSvc = inject(StoreService);
  private translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly currencySvc = inject(CurrencyService);

  /**
   * Kassa qaysi valyutada ko'rsatilayotgani. Kassa valyuta bo'yicha BO'LINADI —
   * dollar bilan so'mni bitta songa qo'shib bo'lmaydi (I11).
   */
  selectedCurrency = signal<string>('');

  onCurrencyChange(code: string): void {
    if (code === this.selectedCurrency()) return;
    this.selectedCurrency.set(code);
    this.loadBalance();
    this.loadFlow();
  }

  balance = signal<CashBalanceDto | null>(null);
  entries = signal<CashFlowEntryDto[]>([]);
  totalIn = signal(0);
  totalOut = signal(0);
  loading = signal(false);

  /**
   * Qoldiq kartalari alohida so'rovdan keladi va `loading` (pul oqimi jadvali) bilan
   * bir vaqtda tugamaydi. Bitta bayroq ishlatilsa kartalar jadval yuklanib bo'lgach
   * ham skeletda qolib ketardi.
   */
  balanceLoading = signal(false);

  /** Skeleton kartalar soni — pastdagi haqiqiy kartalar bilan bir xil. */
  readonly balanceSkeletons = [1, 2, 3, 4, 5];

  stores = signal<StoreLiteDto[]>([]);
  filterStoreId: string | null = null;

  /** Bitta do'konli biznesda tanlagichlar yashiriladi — `storePicker` ga qarang. */
  readonly storePick = storePicker(this.stores);
  range: Date[] = [];

  // Manual transaction dialog
  txVisible = false;
  txSaving = false;
  txForm: {
    storeId: string | null;
    type: CashTransactionType;
    amount: number;
    paymentMethod: PaymentMethod;
    description: string;
    currency: string;
  } = {
    storeId: null,
    type: 'OwnerDeposit',
    amount: 0,
    paymentMethod: 'Cash',
    description: '',
    currency: '',
  };

  txTypes: { label: string; value: CashTransactionType }[] = [
    { label: 'CASH.TX_TYPE.OwnerDeposit', value: 'OwnerDeposit' },
    { label: 'CASH.TX_TYPE.OwnerWithdrawal', value: 'OwnerWithdrawal' },
  ];
  paymentMethods: { label: string; value: PaymentMethod }[] = [
    { label: 'FINANCE.METHOD.Cash', value: 'Cash' },
    { label: 'FINANCE.METHOD.Card', value: 'Card' },
    { label: 'FINANCE.METHOD.Bank', value: 'Bank' },
  ];

  totalBalance(balance: CashBalanceDto): number {
    return balance.cashBalance + balance.cardNet + balance.bankNet;
  }

  ngOnInit() {
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    this.range = [monthStart, today];
    this.storeSvc
      .getAllLite()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => this.stores.set(res.data ?? []));
    this.loadBalance();
    this.loadFlow();
  }

  loadBalance() {
    this.balanceLoading.set(true);
    this.svc
      .getBalance(this.filterStoreId ?? undefined, this.selectedCurrency() || undefined)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.balanceLoading.set(false)),
      )
      .subscribe((res) => {
        this.balance.set(res.data);
        // Backend qaysi valyutani qo'llaganini aytadi — tanlagich shunga moslashadi.
        if (res.data?.currency) this.selectedCurrency.set(res.data.currency);
      });
  }

  loadFlow() {
    const from = this.range?.[0];
    const to = this.range?.[1];
    if (!from || !to) return;
    const end = new Date(to);
    end.setHours(23, 59, 59, 999);
    this.loading.set(true);
    this.svc
      .getFlow(
        from.toISOString(),
        end.toISOString(),
        this.filterStoreId ?? undefined,
        this.selectedCurrency() || undefined,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.entries.set(res.data?.entries ?? []);
          this.totalIn.set(res.data?.totalIn ?? 0);
          this.totalOut.set(res.data?.totalOut ?? 0);
        },
        complete: () => this.loading.set(false),
      });
  }

  onFilterChange() {
    this.loadBalance();
    this.loadFlow();
  }

  openTx() {
    this.txForm = {
      storeId: this.filterStoreId ?? this.stores()[0]?.id ?? null,
      type: 'OwnerDeposit',
      amount: 0,
      paymentMethod: 'Cash',
      description: '',
      // Hozir ko'rilayotgan daftar — kassir odatda shunga pul qo'shadi.
      currency: this.selectedCurrency() || this.currencySvc.defaultCode(),
    };
    this.txVisible = true;
  }

  saveTx() {
    // Tanlagich yashirilgan bo'lsa qiymat `openTx()` da qo'yiladi; do'kon ro'yxati
    // kechikib kelgan holat uchun zaxira.
    this.txForm.storeId ??= this.storePick.single()?.id ?? null;

    if (!this.txForm.storeId) {
      this.svc.showWarn(this.translate.instant('CASH.VALIDATION.STORE'));
      return;
    }
    if (this.txForm.amount <= 0) {
      this.svc.showWarn(this.translate.instant('CASH.VALIDATION.AMOUNT'));
      return;
    }
    this.txSaving = true;
    this.svc
      .addTransaction({
        storeId: this.txForm.storeId,
        type: this.txForm.type,
        amount: this.txForm.amount,
        paymentMethod: this.txForm.paymentMethod,
        isInflow: this.txForm.type !== 'OwnerWithdrawal',
        description: this.txForm.description || null,
        currency: this.txForm.currency || null,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.svc.showSuccess();
          this.txVisible = false;
          this.onFilterChange();
        },
        error: () => (this.txSaving = false),
        complete: () => (this.txSaving = false),
      });
  }

  sourceSeverity(direction: string): 'success' | 'danger' {
    return direction === 'In' ? 'success' : 'danger';
  }
}
